import { useConversationSidePanelContext } from "@app/components/assistant/conversation/ConversationSidePanelContext";
import { InternalActionIcons } from "@app/components/resources/resources_icons";
import { useWorkReceipt } from "@app/hooks/conversations/useWorkReceipt";
import type { WorkReceiptToolCall } from "@app/types/assistant/work_receipt";
import {
  CheckCircle,
  ChevronRight,
  cn,
  Icon,
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
  Tooltip,
} from "@dust-tt/sparkle";
import type { ReactElement } from "react";
import { useCallback, useId, useState } from "react";

export function formatRunDuration(durationMs: number): string {
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return minutes > 0
    ? `${minutes}:${String(seconds).padStart(2, "0")}`
    : `${seconds}s`;
}

export function formatTokenCount(tokens: number): string {
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(1)}M tokens`;
  }
  if (tokens >= 1_000) {
    return `${(tokens / 1_000).toFixed(1)}k tokens`;
  }

  return `${tokens} tokens`;
}

interface ToolCallRowProps {
  toolCall: WorkReceiptToolCall;
  onOpenDetail: ((actionId: string) => void) | null;
}

/**
 * The label is the action's own resolved display label, so a row reads the way the
 * activity-timeline step for the same call reads — three delegate calls are three
 * questions, not three copies of the tool's name. Envelopes written before the tag
 * existed carry neither label nor action, so those rows keep the tool name and stay
 * inert rather than linking nowhere.
 */
function ToolCallRow({ toolCall, onOpenDetail }: ToolCallRowProps) {
  const body = (
    <>
      <Icon
        visual={CheckCircle}
        size="xs"
        className="shrink-0 text-success-500"
      />
      <span className="min-w-0 flex-1 truncate font-medium text-foreground">
        {toolCall.label ?? toolCall.toolName}
      </span>
    </>
  );

  const rowClassName =
    "flex min-h-8 w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm";

  if (!toolCall.actionId || !onOpenDetail) {
    return <li className={rowClassName}>{body}</li>;
  }

  const { actionId } = toolCall;

  return (
    <li>
      <button
        type="button"
        className={cn(rowClassName, "hover:bg-muted-background")}
        onClick={() => onOpenDetail(actionId)}
      >
        {body}
        <Icon visual={ChevronRight} size="xs" className="shrink-0" />
      </button>
    </li>
  );
}

interface WorkReceiptPopoverProps {
  conversationId: string;
  messageId: string;
  narration: string | null;
  trigger: ReactElement;
  workspaceId: string;
}

/**
 * "See my work" for one agent message. The tool list is the message's own audit
 * envelope rows read back, so what the user reads here and what the audit trail
 * holds cannot diverge.
 */
export function WorkReceiptPopover({
  conversationId,
  messageId,
  narration,
  trigger,
  workspaceId,
}: WorkReceiptPopoverProps) {
  const headingId = useId();
  const { togglePanel } = useConversationSidePanelContext();
  const [hasOpened, setHasOpened] = useState(false);
  const openDetail = useCallback(
    (actionId: string) => togglePanel({ type: "actions", messageId, actionId }),
    [togglePanel, messageId]
  );
  const { receipt, isReceiptLoading, mutateReceipt } = useWorkReceipt({
    conversationId,
    workspaceId,
    messageId,
    disabled: !hasOpened,
  });

  return (
    <PopoverRoot
      onOpenChange={(open) => {
        if (!open) {
          return;
        }
        if (hasOpened) {
          void mutateReceipt();
        } else {
          setHasOpened(true);
        }
      }}
    >
      <Tooltip
        label="See my work"
        tooltipTriggerAsChild
        trigger={<PopoverTrigger asChild>{trigger}</PopoverTrigger>}
      />
      <PopoverContent
        role="dialog"
        aria-labelledby={headingId}
        align="start"
        className="w-96 p-2"
        preventAutoFocusOnClose={false}
      >
        <h2 id={headingId} className="px-2 py-1 text-sm font-semibold">
          Work receipt
        </h2>

        {receipt?.requestedBy && (
          <p className="flex items-center gap-2 px-2 pb-1 text-xs text-muted-foreground">
            <Icon
              visual={InternalActionIcons.ActionRobotIcon}
              size="xs"
              className="shrink-0"
            />
            Delegated by another agent — this answer is attributed to its
            author, not to the agent that asked for it.
          </p>
        )}

        {narration && (
          <p className="px-2 py-1 text-sm italic text-muted-foreground">
            {narration}
          </p>
        )}

        {isReceiptLoading && !receipt ? (
          <div
            aria-busy="true"
            aria-live="polite"
            className="flex min-h-8 items-center px-2 py-1.5 text-sm text-muted-foreground"
          >
            <span className="flex-1">Loading work</span>
            <span className="h-3 w-8 animate-pulse rounded bg-muted-foreground/20" />
          </div>
        ) : receipt ? (
          <>
            <h3
              className="px-2 py-1 text-xs font-medium text-muted-foreground"
              id={`${headingId}-tools`}
            >
              {receipt.toolCalls.length}{" "}
              {receipt.toolCalls.length === 1 ? "tool call" : "tool calls"}
            </h3>
            {receipt.toolCalls.length > 0 && (
              <ul aria-labelledby={`${headingId}-tools`}>
                {receipt.toolCalls.map((toolCall, index) => (
                  <ToolCallRow
                    key={`${toolCall.toolName}-${toolCall.executedAt}-${index}`}
                    toolCall={toolCall}
                    onOpenDetail={openDetail}
                  />
                ))}
              </ul>
            )}

            <hr className="my-1 border-t border-border" />

            <p className="px-2 py-1 text-xs text-muted-foreground">
              Ran for {formatRunDuration(receipt.durationMs)}
              {receipt.tokens !== null &&
                ` · ${formatTokenCount(receipt.tokens)}`}
            </p>
          </>
        ) : (
          <p className="px-2 py-1.5 text-sm text-muted-foreground">
            No work recorded for this message.
          </p>
        )}
      </PopoverContent>
    </PopoverRoot>
  );
}
